import {useMemo, useState} from 'react';
import {Select, Typography} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';

import useCorpAccountSelection from '@/components/global/shared/CorpAccountDropdown/useCorpAccountSelection';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';

type WhereCorpAccountSelectProps = {
  onDropdownClose: () => void;
  onNavigateOut: (direction: 'up' | 'down') => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
};

const WhereCorpAccountSelect: React.FC<WhereCorpAccountSelectProps> = ({
  onDropdownClose,
  onNavigateOut,
  triggerRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {t} = useTranslation('app-bar');
  const {sortedCorpAccounts, currentCorpAccountUuid, changeSelectedCorpAccountUuid} =
    useCorpAccountSelection();
  const {trackOrderAccount} = useEventBarV2Tracking();

  // `Select.Value` renders the raw value unless `Select.Root` is given the item labels.
  const selectItems = useMemo(
    () =>
      sortedCorpAccounts.map(account => ({
        label: account.displayName || account.name,
        value: account.uuid,
      })),
    [sortedCorpAccounts],
  );

  if (sortedCorpAccounts.length <= 1) return null;

  const handleValueChange = async (uuid: string) => {
    const account = sortedCorpAccounts.find(corpAccount => corpAccount.uuid === uuid);
    trackOrderAccount({corpAccountId: account?.id ?? null, isParent: account?.isParent ?? null});
    await changeSelectedCorpAccountUuid(uuid);
  };

  // Arrow keys drive the Where panel's roving navigation, not the Select. While the menu is
  // closed, intercept Up/Down and hand roving focus back to the Where input. Enter/Space/typeahead
  // still open the menu.
  //
  // Base UI chains our handler with its own and runs ours first; `preventDefault()` does not stop
  // Base UI's, so the Select would still open on arrow. `preventBaseUIHandler()` is the documented
  // way to opt out of it.
  const handleTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement> & {preventBaseUIHandler?: () => void},
  ) => {
    if (isOpen) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventBaseUIHandler?.();
      event.preventDefault();
      event.stopPropagation();
      onNavigateOut(event.key === 'ArrowUp' ? 'up' : 'down');
    }
  };

  return (
    <div className="mb-3 flex flex-col gap-1">
      <Typography variant="body-sm" className="font-semibold text-black-olive">
        {t('eventBarV2.where.corpAccountLabel')}
      </Typography>
      <Select.Root
        items={selectItems}
        onOpenChange={open => {
          setIsOpen(open);
          // Return focus to the Where input (the panel's focus hub) rather than the trigger, on
          // both selection and Escape. `finalFocus={false}` below stops Base UI moving it back.
          if (!open) onDropdownClose();
        }}
        onValueChange={value => {
          if (value) void handleValueChange(value);
        }}
        value={currentCorpAccountUuid}
      >
        <Select.Trigger
          aria-label={t('eventBarV2.where.corpAccountLabel')}
          className="w-full"
          data-testid="where-corp-account-select"
          onKeyDown={handleTriggerKeyDown}
          ref={triggerRef}
        >
          <Select.Value placeholder={t('eventBarV2.where.corpAccountPlaceholder')} />
        </Select.Trigger>
        <Select.Content data-event-bar-surface finalFocus={false}>
          {sortedCorpAccounts.map(account => (
            <Select.Item key={account.uuid} value={account.uuid}>
              {account.displayName || account.name}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </div>
  );
};

export default WhereCorpAccountSelect;
